import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

function isDesktopNav() {
  return window.matchMedia('(min-width: 981px)').matches;
}

const LOGO_SRC = `${process.env.PUBLIC_URL}/Assets/Images/Website_logo_color logo-black text-1.png`;

const applicationLinks = [
  { label: 'Procure to Pay', to: '/procure-to-pay' },
  { label: 'Inventory Management', to: '/inventory-management' },
  { label: 'Master Data Solution', to: '/master-data-solution' },
];

const applicationPaths = ['/applications', '/procure-to-pay', '/inventory-management', '/master-data-solution'];

const serviceLinks = [
  { label: 'Procurement Services', to: '/procurement-services' },
  { label: 'Co-managed Services', to: '/co-managed-services' },
  { label: 'Tail-Spend Management', to: '/tail-spend-management' },
  { label: 'Inventory Services', to: '/inventory-services' },
  { label: 'Procurement Consulting', to: '/procurement-consulting' },
];

const servicePaths = ['/services', '/procurement-services', '/co-managed-services', '/tail-spend-management', '/inventory-services', '/procurement-consulting'];

function Header() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const hoverLocked = useRef(false);
  const leaveTimer = useRef(0);

  useEffect(() => {
    window.clearTimeout(leaveTimer.current);
    setOpen(false);
    setOpenMenu('');
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [location.pathname, location.hash]);

  useEffect(() => () => window.clearTimeout(leaveTimer.current), []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setOpenMenu('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const closeNav = () => {
    hoverLocked.current = true;
    window.clearTimeout(leaveTimer.current);
    setOpen(false);
    setOpenMenu('');
  };

  const toggleMenu = (name) => {
    hoverLocked.current = false;
    setOpenMenu((current) => (current === name ? '' : name));
  };

  const enterMenu = (name) => {
    if (!isDesktopNav()) return;
    window.clearTimeout(leaveTimer.current);
    if (hoverLocked.current) return;
    setOpenMenu(name);
  };

  const leaveMenu = () => {
    if (!isDesktopNav()) return;
    window.clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(() => {
      hoverLocked.current = false;
      setOpenMenu('');
    }, 80);
  };

  return (
    <header className={`site-header ${scrolled ? 'is-scrolled' : ''} ${open ? 'is-open' : ''}`}>
      <div className="header-shell">
        <div className="container header-bar">
          <Link to="/" className="header-brand" aria-label="InstaTenders home" onClick={closeNav}>
            <img src={LOGO_SRC} alt="InstaTenders" className="header-logo" width="400" height="100" decoding="async" />
          </Link>

          <nav className={`header-nav ${open ? 'is-open' : ''}`} aria-label="Primary" id="site-navigation">
            <NavLink to="/" end className="nav-link" onClick={closeNav}>
              Home
            </NavLink>

            <div
              className={`nav-item ${openMenu === 'applications' ? 'is-open' : ''}`}
              onMouseEnter={() => enterMenu('applications')}
              onMouseLeave={leaveMenu}
            >
              <button
                type="button"
                className={`nav-link nav-trigger${applicationPaths.includes(location.pathname) ? ' active' : ''}`}
                aria-expanded={openMenu === 'applications'}
                aria-controls="nav-applications"
                onClick={() => toggleMenu('applications')}
              >
                Applications
                <span className="chevron" aria-hidden="true" />
              </button>
              <div className="nav-dropdown" id="nav-applications">
                <div className="nav-dropdown-inner">
                  <NavLink to="/applications" className="dropdown-link" onClick={closeNav}>
                    Overview
                  </NavLink>
                  {applicationLinks.map((item) => {
                    const NavItem = item.to.includes('#') ? Link : NavLink;
                    return (
                      <NavItem key={item.to} to={item.to} className="dropdown-link" onClick={closeNav}>
                        {item.label}
                      </NavItem>
                    );
                  })}
                </div>
              </div>
            </div>

            <div
              className={`nav-item ${openMenu === 'services' ? 'is-open' : ''}`}
              onMouseEnter={() => enterMenu('services')}
              onMouseLeave={leaveMenu}
            >
              <button
                type="button"
                className={`nav-link nav-trigger${servicePaths.includes(location.pathname) ? ' active' : ''}`}
                aria-expanded={openMenu === 'services'}
                aria-controls="nav-services"
                onClick={() => toggleMenu('services')}
              >
                Services
                <span className="chevron" aria-hidden="true" />
              </button>
              <div className="nav-dropdown" id="nav-services">
                <div className="nav-dropdown-inner">
                  <NavLink to="/services" className="dropdown-link" onClick={closeNav}>
                    Overview
                  </NavLink>
                  {serviceLinks.map((item) => {
                    const NavItem = item.to.includes('#') ? Link : NavLink;
                    return (
                      <NavItem key={item.to} to={item.to} className="dropdown-link" onClick={closeNav}>
                        {item.label}
                      </NavItem>
                    );
                  })}
                </div>
              </div>
            </div>

            <NavLink to="/connect" className="nav-link" onClick={closeNav}>
              Connect
            </NavLink>
            <NavLink to="/careers" className="nav-link" onClick={closeNav}>
              Careers
            </NavLink>

            <Link to="/contact" className="btn btn-green header-contact mobile-only" onClick={closeNav}>
              Contact
            </Link>
          </nav>

          <Link to="/contact" className="btn btn-green header-contact desktop-only">
            Contact
          </Link>

          <button
            type="button"
            className={`menu-toggle ${open ? 'is-open' : ''}`}
            aria-label={open ? 'Close navigation' : 'Open navigation'}
            aria-expanded={open}
            aria-controls="site-navigation"
            onClick={() => setOpen((value) => !value)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>
      <button
        type="button"
        className={`nav-backdrop${open ? ' is-open' : ''}`}
        tabIndex={-1}
        aria-label="Close navigation"
        aria-hidden={!open}
        onClick={closeNav}
      />
    </header>
  );
}

export default Header;
